import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import useBlogStore from '../../store/admin/blogStore';
import { createBlog, deleteBlog, getBlogById, getBlogs, updateBlog } from '../../api/admin/blogApi';
import { useNavigate } from 'react-router-dom';


export const useBlogs = () => {
  const { setError } = useBlogStore();

  const {
    data: blogs,
    isLoading,
    error,
    refetch
  } = useQuery('blogs', getBlogs, {
    onError: (error) => {
      setError(error?.response?.data?.message || 'Failed to fetch blogs');
    },
    retry: false,
    staleTime: 300000, // 5 minutes
    cacheTime: 3600000, // 1 hour
  });

  return {
    blogs,
    isLoading,
    error,
    refetchBlogs: refetch
  };
};

export const useBlogById = (id) => {
  const { setError } = useBlogStore();

  return useQuery(['blog', id], () => getBlogById(id), {
    onError: (error) => {
      setError(error?.response?.data?.message || 'Failed to fetch blog');
    },
    enabled: !!id,
    retry: false,
  });
};

export const useCreateBlog = () => {
  const queryClient = useQueryClient();
  const { setError } = useBlogStore();
  const navigate = useNavigate();
  
  const validateBlog = (blogData) => {
    if (!blogData.title?.trim()) {
      throw new Error('Title is required!');
    }
    if (!blogData.content?.trim()) {
      throw new Error('Content is required!');
    }
    
    // Optional: Add image validation
    if (blogData.featuredImage && !(blogData.featuredImage instanceof File)) {
      throw new Error('Featured image must be a valid file!');
    }
    
    if (blogData.coverImage && !(blogData.coverImage instanceof File)) {
      throw new Error('Cover image must be a valid file!');
    }
  };
  
  return useMutation(createBlog, {
    onMutate: (variables) => {
      try {
        validateBlog(variables);
      } catch (error) {
        toast.error(error.message);
        return Promise.reject(error);
      }
    },
    onSuccess: () => {
      toast.success('Blog created successfully!');
      queryClient.invalidateQueries('blogs');
      navigate(-1);
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to create blog';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });
};

export const useUpdateBlog = () => {
  const queryClient = useQueryClient();
  const { setError } = useBlogStore();

  return useMutation(updateBlog, {
    onSuccess: (data, variables) => {
      toast.success('Blog updated successfully!');
      queryClient.invalidateQueries(['blog', variables.id]);
      queryClient.invalidateQueries('blogs');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to update blog';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });
};

export const useDeleteBlog = () => {
  const queryClient = useQueryClient();
  const { setError } = useBlogStore();

  return useMutation(deleteBlog, {
    onSuccess: () => {
      toast.success('Blog deleted successfully!');
      queryClient.invalidateQueries('blogs');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to delete blog';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });
};